import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { useRouter } from "next/compat/router";
import dynamic from "next/dynamic";
import posthog from "posthog-js";

import { useAccess, useUserAction } from "~/hooks";
import useCta from "~/hooks/useCta";
import useOnboardingChecklist from "~/hooks/useOnboardingChecklist";
import usePlanConfig from "~/hooks/usePlanConfig";
import useSale from "~/hooks/useSale";
import useTrackCTA from "~/hooks/useTrackCTA";
import saleAnimation from "~/public/lottie-animations/sale.json";
import { restrictedPaths } from "~/utils/constants";

const Lottie = dynamic(
	() => import("lottie-react").then((mod) => mod.default),
	{ ssr: false },
);

const ExpressBanner = ({ hasAppBar }: { hasAppBar?: boolean }) => {
	const { isLoaded, userId } = useAuth();
	const { planConfig } = usePlanConfig();
	const { sale, isLoadingSale } = useSale();
	const { setUserAction } = useUserAction();
	const { isLoadingAccess, isPaused, isMember } = useAccess();
	const router = useRouter();
	const trackCTA = useTrackCTA();
	const { cta, isCtaEnabled, hasCta } = useCta();
	const { checklist, isLoadingChecklist, isDismissed, numberOfRemainingSteps } =
		useOnboardingChecklist();

	const isHidden =
		restrictedPaths.includes(router?.pathname!) ||
		!isLoaded ||
		isLoadingAccess ||
		isPaused ||
		isLoadingSale ||
		(isMember && !sale?.isAnnualUpgrade);

	if (isHidden) return null;

	const showOnboarding =
		!!userId &&
		!isMember &&
		!sale &&
		!isLoadingChecklist &&
		!isDismissed &&
		!!checklist &&
		!checklist.every((item) => item.isCompleted);

	// The learning-plan page already renders the onboarding checklist inline, so
	// suppress the onboarding version of the banner there to avoid duplication.
	if (showOnboarding && router?.pathname === "/learning-plan") return null;

	const colorToStylesMap: {
		[key: string]: {
			background: string;
			text: string;
			secondaryText: string;
			button?: string;
		};
	} = {
		control: {
			background: "bg-white dark:bg-slate-700",
			text: "text-sky-700 dark:text-sky-200",
			secondaryText: "text-slate-700 dark:text-slate-300",
			button:
				"bg-gradient-to-r from-sky-500 via-sky-700 to-sky-500 hover:from-sky-600 hover:via-sky-800 hover:to-sky-600 text-white shadow-slate-500 animated-background",
		},
		blue: {
			background:
				"bg-gradient-to-r from-sky-500 via-sky-700 to-sky-500 animated-background-slow",
			text: "text-white",
			secondaryText: "text-white",
			button: "bg-white text-slate-700 hover:bg-slate-100",
		},
		dark: {
			background:
				"bg-gradient-to-r from-yellow-950 via-sky-950 to-yellow-950 animated-background",
			text: "text-white",
			secondaryText: "text-white",
			button: "bg-white text-yellow-800 hover:bg-slate-100",
		},
	};

	const selectedStyles = userId
		? sale
			? colorToStylesMap.dark // If a sale is on use dark theme
			: colorToStylesMap.blue // If user is logged in (subscription) use blue theme
		: colorToStylesMap.control; // If logged out (signup) use white control theme

	return (
		<div
			className={clsx(
				"sticky right-0 left-0 z-20 border-t shadow-xl xl:bottom-0",
				hasAppBar
					? "bottom-[3.65rem] shadow-none! tablet:shadow-xl"
					: "bottom-0",
				selectedStyles.background,
			)}
		>
			<div
				className={clsx(
					"mx-auto flex w-full max-w-screen-2xl items-center justify-between gap-4 px-4 py-2 xl:px-6 xl:py-1.5",
				)}
			>
				<div className="flex items-center gap-4 xl:gap-2">
					{sale && (
						<div className="tablet:block hidden h-12 overflow-visible">
							<Lottie
								animationData={saleAnimation}
								loop
								className="-my-4 laptop:w-24 w-20 xl:-my-6 dark:brightness-90"
							/>
						</div>
					)}

					<div className="flex flex-col gap-0.5 text-left">
						<h3
							className={clsx(
								"font-semibold phablet:text-xl",
								selectedStyles.text,
							)}
						>
							{showOnboarding ? "Finish setting up your account" : cta.title}
						</h3>

						<p
							className={clsx(
								"flex phablet:font-medium phablet:text-base text-sm dark:text-white",
								selectedStyles.secondaryText,
							)}
						>
							{showOnboarding ? (
								`${numberOfRemainingSteps} steps remaining to get started`
							) : planConfig.trialLength ? (
								<div className="-ml-1 rounded-lg border border-amber-100 bg-amber-50 px-1 py-0 font-medium text-amber-900 dark:border-amber-300/30 dark:bg-amber-300/20 dark:text-amber-100">
									Sign-up now for a FREE 7-Day Trial
								</div>
							) : (
								cta.description
							)}
						</p>
					</div>
				</div>
				<div className="flex gap-3">
					<button
						onClick={() => {
							if (showOnboarding) {
								posthog.capture("cta:onboarding_checklist_banner_clicked", {
									numberOfRemainingSteps,
								});
								trackCTA(
									UserAction.OPEN_ONBOARDING_CHECKLIST,
									"Express Banner",
								);
								setUserAction(UserAction.OPEN_ONBOARDING_CHECKLIST);
								return;
							}

							if (userId) {
								posthog.capture("cta:membership_banner_clicked", {
									hasCta,
									isCtaEnabled,
								});
							} else {
								posthog.capture("cta:signup_banner_clicked", {
									hasCta,
									isCtaEnabled,
								});
							}

							if (cta.redirectButton) {
								router?.push(cta.buttonRedirectURL!);
								return;
							}

							trackCTA(cta.userAction, "Express Banner");
							setUserAction(cta.userAction);
						}}
						className={clsx(
							"inset-ring-1 inset-ring-gray-500/80 whitespace-nowrap rounded-full px-5 py-2 font-medium shadow-md",
							selectedStyles.button,
						)}
					>
						{showOnboarding ? (
							"Get Started"
						) : sale ? (
							<>
								<span className="phablet:inline-block hidden">
									{sale.buttonText}
								</span>
								<span className="inline-block phablet:hidden">25% Off</span>
							</>
						) : (
							<>
								<span className="phablet:inline-block hidden">
									{cta.buttonText}
								</span>
								<span className="inline-block phablet:hidden">
									{cta.mobileButtonText ?? cta.buttonText}
								</span>
							</>
						)}
					</button>
				</div>
			</div>
		</div>
	);
};

export default ExpressBanner;
